import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag, Row, Col,
  Modal, Form, message, InputNumber, DatePicker, Statistic,
} from 'antd'
import { FileTextOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const STATUS_COLORS: Record<string, string> = {
  UNPAID: 'error',
  PARTIAL: 'warning',
  PAID: 'success',
  OVERDUE: 'red',
}

export default function InvoicePage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [customerFilter, setCustomerFilter] = useState<string | undefined>()
  const [paymentModal, setPaymentModal] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [paymentForm] = Form.useForm()

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['invoices', plantId, statusFilter, customerFilter],
    queryFn: () =>
      apiClient.get(`${API}/invoices`, {
        params: {
          plant_id: plantId,
          status: statusFilter || undefined,
          customer_id: customerFilter || undefined,
          limit: 200,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: customers } = useQuery({
    queryKey: ['customers', plantId],
    queryFn: () =>
      apiClient.get(`${API}/customers`, { params: { plant_id: plantId, limit: 500 } }).then(r => r.data),
    enabled: !!plantId,
  })

  const paymentMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.post(`${API}/invoices/${id}/payment`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      message.success('Payment recorded')
      setPaymentModal(null)
      paymentForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const invoiceList = Array.isArray(invoices) ? invoices : []
  const customerList = Array.isArray(customers) ? customers : []

  // Summary stats
  const totalInvoiced = invoiceList.reduce((s: number, i: any) => s + Number(i.total_invoice ?? 0), 0)
  const totalReceived = invoiceList.reduce((s: number, i: any) => s + Number(i.amount_received ?? 0), 0)
  const totalOutstanding = invoiceList.reduce((s: number, i: any) => s + Number(i.outstanding ?? 0), 0)
  const overdueCount = invoiceList.filter((i: any) => {
    if (!i.payment_due_date) return false
    return dayjs(i.payment_due_date).isBefore(dayjs(), 'day') && i.status !== 'PAID'
  }).length

  const columns = [
    {
      title: 'Invoice No', dataIndex: 'invoice_no', key: 'invoice_no', width: 140,
      render: (v: string) => <b style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</b>,
    },
    {
      title: 'Invoice Date', dataIndex: 'invoice_date', key: 'invoice_date', width: 120,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—',
    },
    {
      title: 'Customer', key: 'customer', width: 160,
      render: (_: unknown, r: any) => r.customer_name ?? r.customer?.customer_name ?? '—',
    },
    {
      title: 'Dispatch No', key: 'dispatch_no', width: 130,
      render: (_: unknown, r: any) => r.dispatch_no ?? r.dispatch_order?.dispatch_no ?? '—',
    },
    {
      title: 'Taxable (₹)', dataIndex: 'taxable_value', key: 'taxable_value', width: 120, align: 'right' as const,
      render: (v: number) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—',
    },
    {
      title: 'Total Invoice (₹)', dataIndex: 'total_invoice', key: 'total_invoice', width: 130, align: 'right' as const,
      render: (v: number) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—',
    },
    {
      title: 'Outstanding (₹)', dataIndex: 'outstanding', key: 'outstanding', width: 130, align: 'right' as const,
      render: (v: number) => {
        const amt = Number(v ?? 0)
        return (
          <span style={{ color: amt > 0 ? 'red' : '#52c41a', fontWeight: amt > 0 ? 600 : 400 }}>
            ₹{amt.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </span>
        )
      },
    },
    {
      title: 'Due Date', dataIndex: 'payment_due_date', key: 'payment_due_date', width: 110,
      render: (v: string, r: any) => {
        if (!v) return '—'
        const isOverdue = dayjs(v).isBefore(dayjs(), 'day') && r.status !== 'PAID'
        return <span style={{ color: isOverdue ? 'red' : 'inherit' }}>{dayjs(v).format('DD-MMM-YY')}</span>
      },
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 140,
      render: (_: unknown, r: any) => (
        r.status !== 'PAID' ? (
          <Button
            size="small"
            type="primary"
            onClick={() => {
              setPaymentModal(r)
              paymentForm.setFieldsValue({ payment_date: dayjs() })
            }}
          >
            Record Payment
          </Button>
        ) : null
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <FileTextOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Invoices
      </Title>
      <Typography.Text type="secondary">Invoice tracking and payment recording</Typography.Text>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Invoiced (₹)"
              value={totalInvoiced.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              prefix="₹"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Received (₹)"
              value={totalReceived.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              prefix="₹"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Outstanding (₹)"
              value={totalOutstanding.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              prefix="₹"
              valueStyle={{ color: totalOutstanding > 0 ? 'red' : 'inherit' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Overdue Invoices"
              value={overdueCount}
              valueStyle={{ color: overdueCount > 0 ? 'red' : 'inherit' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Customer"
              style={{ width: 200 }}
              value={customerFilter}
              onChange={v => setCustomerFilter(v)}
              options={customerList.map((c: any) => ({ value: c.id, label: c.customer_name }))}
            />
          </Col>
          <Col>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 130 }}
              value={statusFilter}
              onChange={v => setStatusFilter(v)}
              options={Object.keys(STATUS_COLORS).map(s => ({ value: s, label: s }))}
            />
          </Col>
        </Row>

        <Table
          dataSource={invoiceList}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 1300 }}
        />
      </Card>

      {/* Payment Modal */}
      <Modal
        title={`Record Payment — ${paymentModal?.invoice_no}`}
        open={!!paymentModal}
        onCancel={() => { setPaymentModal(null); paymentForm.resetFields() }}
        onOk={() =>
          paymentForm.validateFields().then(vals =>
            paymentMut.mutate({
              id: paymentModal!.id,
              data: {
                ...vals,
                payment_date: vals.payment_date?.format('YYYY-MM-DD'),
              },
            })
          )
        }
        confirmLoading={paymentMut.isPending}
      >
        <Form form={paymentForm} layout="vertical">
          <Form.Item name="payment_amount" label="Payment Amount (₹)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} addonBefore="₹" />
          </Form.Item>
          <Form.Item name="payment_date" label="Payment Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          {paymentModal && (
            <div style={{ color: '#888', fontSize: 12 }}>
              Outstanding: ₹{Number(paymentModal.outstanding ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
          )}
        </Form>
      </Modal>
    </div>
  )
}
